import React from 'react';
import { ProductType, RegisterMode, isKorpoRegisterMode, isTransferRegisterMode } from 'cart-model';
import { useTransRedirect } from 'common-react';
import { useTranslation } from 'react-i18next';
import { batch, useDispatch, useSelector } from 'react-redux';
import { logSelectRetirementType } from '../../model/events';
import ProductWizardState from '../../model/ProductWizardState';
import { productWizardActions } from '../../model/store';
import Screen from '../ProductWizard/Screen';
import { Typography, Button, Card, Link } from 'ui-components';
import { IkeOrIkzeLimits } from './IkeOrIkze';
import { RetirementProductConfig } from './RetirementProductCard.types';

import useStyles from './RetirementProductCard.style';

interface RetirementProductCardProps {
  config: RetirementProductConfig;
  limits: IkeOrIkzeLimits;
  onOpenDialog?: () => void;
  registerEqualHeightRef?: (el: HTMLDivElement | null, groupKey: string) => void;
}

const RetirementProductCard: React.FC<RetirementProductCardProps> = ({
  config,
  limits,
  onOpenDialog,
  registerEqualHeightRef,
}) => {
  const { t } = useTranslation('ike-or-ikze');
  const currentRegisterMode = useSelector((state: ProductWizardState) => state.cartState.registerMode);
  const isCurrentCartKorpo = isKorpoRegisterMode(currentRegisterMode);
  const isCurrentCartTransfer = isTransferRegisterMode(currentRegisterMode);
  const dispatch = useDispatch();
  const redirect = useTransRedirect();
  const c = useStyles({});

  const getTranslationKey = (productType: ProductType): string => productType.toLowerCase();

  const setProductType = (value: ProductType, registerMode: RegisterMode) => {
    batch(() => {
      dispatch(productWizardActions.setProductType(value));
      if (isCurrentCartKorpo || isCurrentCartTransfer) {
        dispatch(productWizardActions.clearCart());
      }
      dispatch(productWizardActions.setKorpoData(undefined));
      dispatch(productWizardActions.setTransferInstitutionName(undefined));
      dispatch(productWizardActions.setSelectedTypeOfInvestment({selectedTypeOfInvestment: value}));
      dispatch(productWizardActions.setRegisterMode(registerMode));
      dispatch(productWizardActions.setIsPensionProcess(true));
    });
  };

  const handleMainButtonClick = (): void => {
    setProductType(config.productType, config.openRegisterMode);
    logSelectRetirementType();
    redirect(Screen.SELECT_PATH.code);
  };

  const handleTransferClick = () => {
    setProductType(config.productType, config.transferRegisterMode);
    logSelectRetirementType();
  };

  const handleKorpoClick = () => {
    setProductType(config.productType, config.korpoRegisterMode);
    logSelectRetirementType();
  };

  const productInfoSections = [
    {
      labelKey: 'box.annual_deposit_limit',
      renderContent: () => config.renderDepositLimit(limits, onOpenDialog)
    },
    {
      labelKey: 'box.tax_benefits',
      renderContent: () => config.renderTaxBenefits(limits)
    },
    {
      labelKey: 'box.fee_for_cancellation',
      renderContent: () => t(`${getTranslationKey(config.productType)}.fee_for_cancellation`)
    },
    {
      labelKey: 'box.payment_of_funds_without_consequences',
      renderContent: () => t(`${getTranslationKey(config.productType)}.payment_of_funds_without_consequences`)
    },
    {
      labelKey: 'box.earlier_withdrawal_of_funds',
      renderContent: () => t(`${getTranslationKey(config.productType)}.earlier_withdrawal_of_funds`)
    }
  ];

  return (
    <Card hoverable className={c.card}>
      <div>
        <Typography variant="caption" component="h5" fontWeight="extraBold" className={c.tableHeader}>
          {t(config.titleKey)}
        </Typography>
      </div>
      <div className={c.productInfoSections}>
        {productInfoSections.map((section) => (
          <div
            key={section.labelKey}
            ref={registerEqualHeightRef ? (el) => registerEqualHeightRef(el, section.labelKey) : undefined}
            className={c.productItemCell}
          >
            <div>
              <Typography variant="body2" fontWeight="bold" textColor="greys.60" className={c.productItemLabel}>
                {t(section.labelKey)}
              </Typography>
              <Typography>
                {section.renderContent()}
              </Typography>
            </div>
          </div>
        ))}
      </div>

      <div className={c.buttonWrapper}>
        <Button accent={true} width="100%" onClick={handleMainButtonClick}>
          {t(config.buttonLabelKey)}
        </Button>
      </div>

      <div className={c.additionalOptionsWrapper}>
        <div className={c.additionalOptionsCell}>
          <Typography variant="body2" textColor="greys.60" className={c.productItemLabel}>
            {t(config.transferLabelKey)}
          </Typography>
          <Link
            variant="underline"
            as="navlink"
            to={config.transferRoute}
            className={c.link}
            onClick={handleTransferClick}
          >
            {t(config.transferButtonLabelKey)}
          </Link>
        </div>
        <div className={c.additionalOptionsCell}>
          <Typography variant="body2" textColor="greys.60" className={c.productItemLabel}>
            {t(config.korpoLabelKey)}
          </Typography>
          <Link
            variant="underline"
            as="navlink"
            to={config.korpoRoute}
            className={c.link}
            onClick={handleKorpoClick}
          >
            {t(config.korpoButtonLabelKey)}
          </Link>
        </div>
      </div>
    </Card>
  );
};

export default RetirementProductCard;
